/**
 * Behaviour for the hero page-builder modules.
 *
 * Three rules this file exists to enforce, all learned from the modules it sits beside:
 *
 * 1. Every instance is initialised independently from its own data- attributes. Nothing is
 *    stored globally and nothing selects across the whole document, so two heroes on one page
 *    can't interfere with each other. (The older PhotoSlideshow hands a multi-element selector
 *    to Swiper, which returns an *array*, and the `.on()` call that follows throws — inside an
 *    untry/caught boot sequence, which leaves the whole site stuck behind the preloader.)
 * 2. Every init is wrapped, so one broken hero can't take down the others or the page.
 * 3. The markup is already correct before this file runs — slide 1 carries `is-active` from
 *    the server. JS only ever *changes* the active slide, it never has to establish one. With
 *    JS disabled or broken the hero is a static, readable image.
 */

const REDUCED_MOTION = () =>
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Run an initialiser for each matching element without letting a failure escape.
 */
function each(selector, fn) {
    const nodes = document.querySelectorAll(selector);

    for (let i = 0; i < nodes.length; i++) {
        try {
            fn(nodes[i]);
        } catch (e) {
            // Deliberately swallowed: a broken hero must not stop the rest of the page
            // booting. Logged so it's still discoverable in the console.
            if (window.console && console.warn) {
                console.warn('[hero] init failed for', selector, e);
            }
        }
    }
}

/**
 * Shared crossfade carousel. Both hero slideshows are the same machine with different
 * chrome, so they share this and pass in which extras they have.
 */
function carousel(root, opts) {
    const slides = root.querySelectorAll('[data-hero-slide]');
    const texts = root.querySelectorAll('[data-hero-text]');
    const goButtons = root.querySelectorAll('[data-hero-go]');
    const total = slides.length;

    if (total === 0) {
        return;
    }

    const delay = Math.max(1000, parseInt(root.getAttribute('data-autoplay'), 10) || opts.defaultDelay);

    let current = 0;
    let timer = null;
    let paused = false;

    function setActive(nodes, index) {
        for (let i = 0; i < nodes.length; i++) {
            const on = i === index;
            nodes[i].classList.toggle('is-active', on);
            // Keep AT in step with what's visually shown.
            if (on) {
                nodes[i].removeAttribute('aria-hidden');
            } else {
                nodes[i].setAttribute('aria-hidden', 'true');
            }
        }
    }

    function paintControls(index) {
        for (let i = 0; i < goButtons.length; i++) {
            goButtons[i].classList.toggle('is-current', i === index);
            goButtons[i].classList.toggle('is-past', i < index);
        }

        // Restart the progress animation on the bar that just became current. Clearing the
        // animation and forcing a reflow is the only reliable way to replay a CSS animation.
        const fill = goButtons[index] ? goButtons[index].querySelector('.hero-slideshow__bar-fill') : null;
        if (fill) {
            fill.style.animation = 'none';
            void fill.offsetWidth;
            fill.style.animation = '';
        }

        // A module with a single continuous bar (no per-slide go-buttons) marks it with
        // data-hero-progress-fill directly on the root instead of nesting it in a go-button.
        // No-op for modules that don't have one.
        //
        // Resets animation-name specifically, not the animation shorthand: the shorthand
        // resets every sub-property it covers (including animation-duration) to its CSS
        // initial value, which would silently wipe the inline animation-duration this
        // element gets from the server (the autoplay-derived timing) down to 0s the first
        // time this runs.
        const progressFill = root.querySelector('[data-hero-progress-fill]');
        if (progressFill) {
            progressFill.style.animationName = 'none';
            void progressFill.offsetWidth;
            progressFill.style.animationName = '';
        }
    }

    function go(index) {
        current = ((index % total) + total) % total;
        setActive(slides, current);
        setActive(texts, current);
        paintControls(current);
    }

    function stop() {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
    }

    function schedule() {
        stop();

        // Reduced motion, a single slide, or a paused hero: never auto-advance. Manual
        // controls keep working in all three cases.
        if (paused || total < 2 || REDUCED_MOTION()) {
            return;
        }

        timer = setTimeout(() => {
            go(current + 1);
            schedule();
        }, delay);
    }

    function setPaused(state) {
        paused = state;
        root.classList.toggle('is-paused', state);
        schedule();
    }

    for (let i = 0; i < goButtons.length; i++) {
        goButtons[i].addEventListener('click', function () {
            go(parseInt(this.getAttribute('data-hero-go'), 10) || 0);
            schedule();
        });
    }

    const prev = root.querySelector('[data-hero-prev]');
    if (prev) {
        prev.addEventListener('click', () => {
            go(current - 1);
            schedule();
        });
    }

    const next = root.querySelector('[data-hero-next]');
    if (next) {
        next.addEventListener('click', () => {
            go(current + 1);
            schedule();
        });
    }

    // Pause on hover per the design, and on keyboard focus too — hover alone is a mouse-only
    // affordance and leaves keyboard users unable to stop the motion.
    root.addEventListener('mouseenter', () => setPaused(true));
    root.addEventListener('mouseleave', () => setPaused(false));
    root.addEventListener('focusin', () => setPaused(true));
    root.addEventListener('focusout', () => setPaused(false));

    // Don't animate a hero nobody is looking at.
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            stop();
        } else {
            schedule();
        }
    });

    paintControls(0);
    schedule();
}

export default function heroModules() {
    each('[data-hero-slideshow]', root => carousel(root, { defaultDelay: 5000 }));
    each('[data-hero-cinematic]', root => carousel(root, { defaultDelay: 6000 }));
    each('[data-hero-frosted]', root => carousel(root, { defaultDelay: 5500 }));
}



// WEBPACK FOOTER //
// ./resources/assets/js/functions/heroModules.js